/**
 * AWS Cognito Hosted UI Service
 * 
 * Handles authentication using Cognito Hosted UI (redirect-based).
 * Exchanges authorization codes for tokens and manages token lifecycle.
 */

/**
 * Cognito configuration from environment variables
 */
const cognitoConfig = {
  domain: import.meta.env.VITE_COGNITO_DOMAIN || '',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
  clientSecret: import.meta.env.VITE_COGNITO_CLIENT_SECRET || '',
  callbackUrl: import.meta.env.VITE_APP_CALLBACK_URL || '',
  logoutUrl: import.meta.env.VITE_APP_LOGOUT_URL || '',
};

/**
 * Validate Cognito configuration
 */
if (!cognitoConfig.domain || !cognitoConfig.clientId || !cognitoConfig.clientSecret) {
  console.error('Missing Cognito configuration. Please check environment variables.');
}

/**
 * Token response from Cognito OAuth endpoint
 */
export interface CognitoTokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * User information extracted from ID token
 */
export interface CognitoUserInfo {
  sub: string;
  email: string;
  email_verified?: boolean;
  name?: string;
  groups: string[];
  tenant?: string; // custom:tenant attribute
}

/**
 * Cognito service for Hosted UI authentication
 */
export const cognitoService = {
  /**
   * Get Cognito Hosted UI login URL
   * 
   * Constructs the URL to redirect users to Cognito login page.
   * 
   * @returns Full URL to Cognito Hosted UI login page
   * 
   * @example
   * ```typescript
   * const loginUrl = cognitoService.getLoginUrl();
   * window.location.href = loginUrl;
   * ```
   */
  getLoginUrl: (): string => {
    const { domain, clientId, callbackUrl } = cognitoConfig;
    
    if (!domain || !clientId || !callbackUrl) {
      throw new Error('Cognito configuration is incomplete');
    }
    
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      redirect_uri: callbackUrl,
      scope: 'email openid profile',
    });
    
    return `https://${domain}/login?${params.toString()}`;
  },

  /**
   * Get Cognito Hosted UI logout URL
   * 
   * Constructs the URL to redirect users to Cognito logout page.
   * 
   * @returns Full URL to Cognito Hosted UI logout page
   * 
   * @example
   * ```typescript
   * const logoutUrl = cognitoService.getLogoutUrl();
   * window.location.href = logoutUrl;
   * ```
   */
  getLogoutUrl: (): string => {
    const { domain, clientId, logoutUrl } = cognitoConfig;
    
    if (!domain || !clientId || !logoutUrl) {
      throw new Error('Cognito configuration is incomplete');
    }
    
    const params = new URLSearchParams({
      client_id: clientId,
      logout_uri: logoutUrl,
    });
    
    return `https://${domain}/logout?${params.toString()}`;
  },

  /**
   * Exchange authorization code for tokens
   * 
   * Exchanges the OAuth authorization code received in callback for Cognito tokens.
   * This is called after Cognito redirects back with a code parameter.
   * 
   * @param code - Authorization code from Cognito callback URL
   * @returns Promise resolving to token response with access, ID, and refresh tokens
   * 
   * @throws {Error} When token exchange fails
   * 
   * @example
   * ```typescript
   * const urlParams = new URLSearchParams(window.location.search);
   * const code = urlParams.get('code');
   * 
   * if (code) {
   *   const tokens = await cognitoService.exchangeCodeForTokens(code);
   *   // Store tokens and redirect
   * }
   * ```
   */
  exchangeCodeForTokens: async (code: string): Promise<CognitoTokenResponse> => {
    const { domain, clientId, clientSecret, callbackUrl } = cognitoConfig;
    
    if (!domain || !clientId || !clientSecret || !callbackUrl) {
      throw new Error('Cognito configuration is incomplete');
    }
    
    const response = await fetch(`https://${domain}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        client_secret: clientSecret,
        code: code,
        redirect_uri: callbackUrl,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', errorText);
      throw new Error(`Failed to exchange code for tokens: ${response.status} - ${errorText}`);
    }
    
    const tokens: CognitoTokenResponse = await response.json();
    return tokens;
  },

  /**
   * Refresh access token using refresh token
   * 
   * Obtains new access and ID tokens using a refresh token.
   * Should be called when access token is expired or about to expire.
   * 
   * @param refreshToken - Refresh token from previous authentication
   * @returns Promise resolving to new token response
   * 
   * @throws {Error} When refresh token is invalid or expired
   * 
   * @example
   * ```typescript
   * const refreshToken = localStorage.getItem('refreshToken');
   * const tokens = await cognitoService.refreshToken(refreshToken);
   * // Update stored tokens
   * ```
   */
  refreshToken: async (refreshToken: string): Promise<CognitoTokenResponse> => {
    const { domain, clientId, clientSecret } = cognitoConfig;
    
    if (!domain || !clientId || !clientSecret) {
      throw new Error('Cognito configuration is incomplete');
    }
    
    const response = await fetch(`https://${domain}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        client_secret: clientSecret,
        refresh_token: refreshToken,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token refresh failed:', errorText);
      throw new Error(`Failed to refresh token: ${response.status} - ${errorText}`);
    }
    
    const tokens: CognitoTokenResponse = await response.json();
    return tokens;
  },

  /**
   * Decode JWT token (without verification)
   * 
   * Decodes a JWT token to extract the payload.
   * 
   * WARNING: This does NOT verify the signature. Token verification
   * should always be done on the backend. Use this only for reading
   * non-sensitive claims like user ID or email.
   * 
   * @param token - JWT token string
   * @returns Decoded token payload or null if decoding fails
   * 
   * @example
   * ```typescript
   * const payload = cognitoService.decodeToken(idToken);
   * console.log(payload.sub); // User ID
   * console.log(payload.email); // User email
   * ```
   */
  decodeToken: (token: string): any => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3 || !parts[1]) {
        console.error('Invalid JWT format: token must have 3 parts');
        return null;
      }
      
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  },

  /**
   * Get user info from ID token
   * 
   * Extracts user information from a Cognito ID token.
   * Includes custom attributes like custom:tenant.
   * 
   * @param idToken - ID token from Cognito
   * @returns User information object or null if token is invalid
   * 
   * @example
   * ```typescript
   * const userInfo = cognitoService.getUserInfo(idToken);
   * console.log(userInfo.sub); // Cognito user ID
   * console.log(userInfo.email); // user@example.com
   * console.log(userInfo.groups); // ['admin', 'users']
   * console.log(userInfo.tenant); // 'tenant-123' from custom:tenant
   * ```
   */
  getUserInfo: (idToken: string): CognitoUserInfo | null => {
    const decoded = cognitoService.decodeToken(idToken);
    
    if (!decoded) {
      return null;
    }
    const user_info = {
      sub: decoded.sub,
      email: decoded.email,
      email_verified: decoded.email_verified,
      name: decoded.name,
      groups: decoded['cognito:groups'] || [],
      tenant: decoded['custom:tenatId'], // Extract custom:tenatId attribute (note: typo in Cognito attribute name)
    };

    console.log(user_info)
    return user_info
  },

  /**
   * Check if token is expired
   * 
   * Checks if a JWT token has expired by comparing the expiration claim
   * with the current time.
   * 
   * @param token - JWT token string
   * @returns true if token is expired or invalid, false if still valid
   * 
   * @example
   * ```typescript
   * if (cognitoService.isTokenExpired(accessToken)) {
   *   // Token expired, refresh it
   *   await cognitoService.refreshToken(refreshToken);
   * }
   * ```
   */
  isTokenExpired: (token: string): boolean => {
    const decoded = cognitoService.decodeToken(token);
    
    if (!decoded?.exp) {
      return true;
    }
    
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    
    return currentTime >= expirationTime;
  },

  /**
   * Redirect to Cognito login page
   * 
   * Convenience function to redirect user to Cognito Hosted UI login.
   * 
   * @example
   * ```typescript
   * cognitoService.redirectToLogin();
   * ```
   */
  redirectToLogin: (): void => {
    const loginUrl = cognitoService.getLoginUrl();
    window.location.href = loginUrl;
  },

  /**
   * Redirect to Cognito logout page
   * 
   * Convenience function to redirect user to Cognito Hosted UI logout.
   * 
   * @example
   * ```typescript
   * cognitoService.redirectToLogout();
   * ```
   */
  redirectToLogout: (): void => {
    const logoutUrl = cognitoService.getLogoutUrl();
    window.location.href = logoutUrl;
  },

  /**
   * Get Cognito configuration
   * 
   * Returns the current Cognito configuration for debugging.
   * 
   * @returns Cognito configuration object
   */
  getConfig: () => cognitoConfig,
};
